import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageParts";
import { Button, Card, Input, Spinner } from "@/components/ui";

export default function Wizard() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [landW, setLandW] = useState("18");
  const [landH, setLandH] = useState("22");
  const [kind, setKind] = useState("villa");
  const [amen, setAmen] = useState([]);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(false);

  const kinds = [
    { id: "villa", ar: "فيلا سكنية", en: "Villa", emoji: "🏠" },
    { id: "apartment", ar: "شقة سكنية", en: "Apartment", emoji: "🏢" },
    { id: "commercial", ar: "محل تجاري", en: "Shop", emoji: "🏪" },
  ];
  const amens = [
    { id: "office", ar: "مكتب", en: "Office" },
    { id: "majlis", ar: "مجلس رجال", en: "Majlis" },
    { id: "storage", ar: "مستودع", en: "Storage" },
    { id: "garage", ar: "كراج", en: "Garage" },
    { id: "pool", ar: "مسبح", en: "Pool" },
  ];

  useEffect(() => {
    if (step !== 4) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.wizardRecommend({ land_w: parseFloat(landW) || 10, land_h: parseFloat(landH) || 10, kind, amenities: amen });
        setRecs(r.items || []);
      } catch (e) {
        toast.error(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [step]); // eslint-disable-line

  const toggle = (a) => setAmen((p) => (p.includes(a) ? p.filter((x) => x !== a) : [...p, a]));

  const generateCustom = () => {
    const genKind = kind === "apartment" ? "apartment" : kind === "commercial" ? "shops" : "villa";
    const q = new URLSearchParams({
      kind: genKind, plot_w: landW, plot_h: landH, floors: "1", bedrooms: "3", bathrooms: "2",
    });
    if (amen.includes("pool")) q.set("has_pool", "true");
    if (amen.includes("garage")) q.set("has_garage", "true");
    if (amen.includes("office")) q.set("has_office", "true");
    navigate(`/variants?${q.toString()}`);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <PageHeader eyebrow={isAr ? "المعالج" : "Wizard"} title={isAr ? "معالج المشروع" : "Project Wizard"} right={<span className="text-sm font-black text-primary">{step}/4</span>} />
      <div className="mb-6 flex gap-1.5">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-primary" : "bg-white/10"}`} />
        ))}
      </div>

      <Card>
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-black">{isAr ? "أبعاد الأرض" : "Land dimensions"}</h2>
            <p className="text-sm text-muted-foreground">{isAr ? "بالمتر - هذه الحدود الخارجية للمبنى" : "In metres - outer building bounds"}</p>
            <div className="grid grid-cols-2 gap-3">
              <Input label={isAr ? "العرض (م)" : "Width (m)"} value={landW} onChange={(e) => setLandW(e.target.value)} data-testid="land-w" />
              <Input label={isAr ? "العمق (م)" : "Depth (m)"} value={landH} onChange={(e) => setLandH(e.target.value)} data-testid="land-h" />
            </div>
            <p className="font-bold text-primary">{isAr ? `الإجمالي: ${(parseFloat(landW) || 0) * (parseFloat(landH) || 0)} م²` : `Total: ${(parseFloat(landW) || 0) * (parseFloat(landH) || 0)} m²`}</p>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            <h2 className="text-lg font-black">{isAr ? "نوع المبنى" : "Building type"}</h2>
            {kinds.map((k) => (
              <button key={k.id} data-testid={`wiz-kind-${k.id}`} onClick={() => setKind(k.id)} className={`flex w-full items-center gap-3 rounded-xl border-2 p-3.5 ${kind === k.id ? "border-primary bg-primary/10" : "border-transparent bg-card/60"}`}>
                <span className="text-2xl">{k.emoji}</span>
                <span className="flex-1 text-start font-black">{isAr ? k.ar : k.en}</span>
                {kind === k.id && <CheckCircle2 className="text-primary" size={20} />}
              </button>
            ))}
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-black">{isAr ? "المرافق المطلوبة" : "Required amenities"}</h2>
            <div className="flex flex-wrap gap-2">
              {amens.map((a) => (
                <button key={a.id} data-testid={`wiz-amen-${a.id}`} onClick={() => toggle(a.id)} className={`rounded-full border px-4 py-2 text-sm font-bold ${amen.includes(a.id) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/60"}`}>
                  {isAr ? a.ar : a.en}
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-4">
            <button onClick={generateCustom} data-testid="wiz-generate" className="w-full rounded-xl bg-primary p-4 text-start text-primary-foreground">
              <div className="flex items-center gap-2 text-base font-black"><Zap size={18} /> {isAr ? "ولّد 10 مخططات مخصّصة الآن" : "Generate 10 custom variants now"}</div>
              <p className="mt-1.5 text-xs opacity-85">{isAr ? `${landW}×${landH} م - يولّد جدران + غرف + أبواب + نوافذ + أعمدة` : `${landW}×${landH} m - auto-generate walls, rooms, doors, windows, columns`}</p>
            </button>
            <div className="h-px bg-border" />
            <h2 className="text-base font-black">{isAr ? "أو اختر من القوالب الجاهزة" : "Or pick a ready template"}</h2>
            {loading ? (
              <Spinner className="text-primary" />
            ) : recs.length === 0 ? (
              <p className="text-sm text-muted-foreground">{isAr ? "لا يوجد قالب مطابق - استخدم زر التوليد بالأعلى" : "No matching templates - use the generate button above"}</p>
            ) : (
              recs.map((r) => (
                <button key={r.id} onClick={() => navigate("/studio")} className="flex w-full items-center justify-between rounded-xl border border-border bg-card/60 p-3.5 text-start">
                  <div>
                    <div className="font-black">{isAr ? r.name_ar : r.name_en}</div>
                    <div className="text-xs text-muted-foreground">{r.land_w}×{r.land_h} م · {r.bedrooms ?? 0} {isAr ? "غرف" : "beds"}</div>
                  </div>
                  <ArrowRight className="text-primary" size={18} />
                </button>
              ))
            )}
          </div>
        )}
      </Card>

      <div className="mt-5 flex justify-between gap-3">
        <Button variant="outline" disabled={step === 1} onClick={() => setStep((s) => s - 1)} data-testid="wiz-back">
          <ArrowLeft size={16} /> {isAr ? "السابق" : "Back"}
        </Button>
        {step < 4 && (
          <Button onClick={() => setStep((s) => s + 1)} data-testid="wiz-next">
            {isAr ? "التالي" : "Next"} <ArrowRight size={16} />
          </Button>
        )}
      </div>
    </div>
  );
}
